'use client';

import clsx from 'clsx';
import { useReducer, useRef } from 'react';
import { Check2, Filter, ThreeDots } from 'react-bootstrap-icons';

import type { InAppNotificationsQuery } from '@repo/frontend-shared/graphql';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSPopover } from '@repo/sds/SDSPopover';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { NotificationIcon } from '@repo/ui/fixtures/header/assets/NotificationIcon';

import { HeaderNotificationContent } from './HeaderNotificationContent';
import { NotificationTabs, type TabType } from './NotificationTabs';

import './HeaderNotificationsSection.scss';

export type NotificationItem = NonNullable<InAppNotificationsQuery['inAppNotifications']['edges'][number]>['node'];

type FilterType = 'all' | 'unread-only' | 'archived';

const FILTER_LABELS: Record<FilterType, string> = {
  all: 'Unread and read',
  'unread-only': 'Unread only',
  archived: 'Archived only',
};

const FILTER_OPTIONS: FilterType[] = ['all', 'unread-only', 'archived'];
const EMPTY_NOTIFICATIONS: NotificationItem[] = [];

interface NotificationPanelState {
  isOpen: boolean;
  selectedTab: TabType;
  isFilterOpen: boolean;
  isThreeDotsOpen: boolean;
  filterType: FilterType;
}

function notificationPanelReducer(
  state: NotificationPanelState,
  changes: Partial<NotificationPanelState>,
): NotificationPanelState {
  return { ...state, ...changes };
}

const INITIAL_NOTIFICATION_PANEL_STATE: NotificationPanelState = {
  isOpen: false,
  selectedTab: 'All',
  isFilterOpen: false,
  isThreeDotsOpen: false,
  filterType: 'all',
};

function getFilterOptionDataTestId(
  option: FilterType,
  ids: {
    filterAllDataTestId?: string;
    filterUnreadDataTestId?: string;
    filterArchivedDataTestId?: string;
  },
): string | undefined {
  if (option === 'all') {
    return ids.filterAllDataTestId;
  }
  if (option === 'unread-only') {
    return ids.filterUnreadDataTestId;
  }
  return ids.filterArchivedDataTestId;
}

interface NotificationFilterMenuProps {
  filterType: FilterType;
  isFilterOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (filter: FilterType) => void;
  filterTriggerDataTestId?: string;
  filterAllDataTestId?: string;
  filterUnreadDataTestId?: string;
  filterArchivedDataTestId?: string;
}

function NotificationFilterMenu({
  filterType,
  isFilterOpen,
  onOpenChange,
  onSelect,
  filterTriggerDataTestId,
  filterAllDataTestId,
  filterUnreadDataTestId,
  filterArchivedDataTestId,
}: NotificationFilterMenuProps) {
  return (
    <SDSPopover
      open={isFilterOpen}
      onOpenChange={onOpenChange}
      side="bottom"
      align="end"
      sideOffset={4}
      className="HeaderNotificationsSection__filter-popover"
      triggerProps={{ 'aria-label': 'Filter notifications' }}
      contentClassName="HeaderNotificationsSection__menu"
      showArrow={false}
      content={
        <SDSFlexbox direction="column" fullWidth>
          {FILTER_OPTIONS.map(option => (
            <SDSPrimitiveButton
              key={option}
              dataTestId={getFilterOptionDataTestId(option, {
                filterAllDataTestId,
                filterUnreadDataTestId,
                filterArchivedDataTestId,
              })}
              className={clsx('HeaderNotificationsSection__menu-option', {
                'HeaderNotificationsSection__menu-option--active': filterType === option,
              })}
              onClick={() => onSelect(option)}
            >
              <SDSFlexbox alignItems="center" justifyContent="space-between" fullWidth>
                <SDSText type="body-medium" text={FILTER_LABELS[option]} color="text-primary" />
                {filterType === option && <Check2 size={16} color="var(--brand-primary, #0bb58d)" />}
              </SDSFlexbox>
            </SDSPrimitiveButton>
          ))}
        </SDSFlexbox>
      }
    >
      <div
        className={clsx('HeaderNotificationsSection__filter-icon', {
          'HeaderNotificationsSection__filter-icon--active': filterType !== 'all',
        })}
        data-testid={filterTriggerDataTestId}
      >
        <Filter size={20} />
      </div>
    </SDSPopover>
  );
}

interface NotificationOptionsMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onMarkAllAsRead: () => void;
  onArchiveAll: () => void;
  moreTriggerDataTestId?: string;
  markAllReadDataTestId?: string;
  archiveAllDataTestId?: string;
}

function NotificationOptionsMenu({
  isOpen,
  onOpenChange,
  onMarkAllAsRead,
  onArchiveAll,
  moreTriggerDataTestId,
  markAllReadDataTestId,
  archiveAllDataTestId,
}: NotificationOptionsMenuProps) {
  return (
    <SDSPopover
      open={isOpen}
      onOpenChange={onOpenChange}
      side="bottom"
      align="end"
      sideOffset={4}
      className="HeaderNotificationsSection__three-dots-popover"
      triggerProps={{ 'aria-label': 'More options' }}
      contentClassName="HeaderNotificationsSection__menu"
      showArrow={false}
      content={
        <SDSFlexbox direction="column" fullWidth>
          <SDSPrimitiveButton
            className="HeaderNotificationsSection__menu-option"
            dataTestId={markAllReadDataTestId}
            onClick={onMarkAllAsRead}
          >
            <SDSText type="body-medium" text="Mark all as read" color="text-primary" />
          </SDSPrimitiveButton>
          <SDSPrimitiveButton
            className="HeaderNotificationsSection__menu-option"
            dataTestId={archiveAllDataTestId}
            onClick={onArchiveAll}
          >
            <SDSText type="body-medium" text="Archive all" color="text-primary" />
          </SDSPrimitiveButton>
        </SDSFlexbox>
      }
    >
      <div className="HeaderNotificationsSection__three-dots-trigger" data-testid={moreTriggerDataTestId}>
        <ThreeDots size={20} />
      </div>
    </SDSPopover>
  );
}

interface NotificationListBodyProps {
  notifications: NotificationItem[];
  filterType: FilterType;
  onMarkAsRead?: (notificationId: string) => void;
  onMarkAsUnread?: (notificationId: string) => void;
  onArchive?: (notificationId: string) => void;
  onUnarchive?: (notificationId: string) => void;
  emptyStateDataTestId?: string;
  notificationItemDataTestId?: string;
  itemUnreadDotDataTestId?: string;
  itemMarkReadDataTestId?: string;
  itemMarkUnreadDataTestId?: string;
  itemArchiveDataTestId?: string;
  itemUnarchiveDataTestId?: string;
}

function NotificationListBody({
  notifications,
  filterType,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  emptyStateDataTestId,
  notificationItemDataTestId,
  itemUnreadDotDataTestId,
  itemMarkReadDataTestId,
  itemMarkUnreadDataTestId,
  itemArchiveDataTestId,
  itemUnarchiveDataTestId,
}: NotificationListBodyProps) {
  if (notifications.length === 0) {
    return (
      <SDSFlexbox justifyContent="center" alignItems="center" fullWidth className="HeaderNotificationsSection__empty">
        <SDSText type="body-medium" text="No notifications" color="muted" dataTestId={emptyStateDataTestId} />
      </SDSFlexbox>
    );
  }

  return (
    <>
      {notifications.map(item => (
        <HeaderNotificationContent
          key={item.id}
          title={item.title}
          dateLabel={new Date(item.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
          message={item.description}
          isUnread={!item.isRead}
          onMarkAsRead={onMarkAsRead ? () => onMarkAsRead(item.id) : undefined}
          onMarkAsUnread={onMarkAsUnread ? () => onMarkAsUnread(item.id) : undefined}
          onArchive={onArchive ? () => onArchive(item.id) : undefined}
          onUnarchive={onUnarchive ? () => onUnarchive(item.id) : undefined}
          isArchivedView={filterType === 'archived'}
          dataTestId={notificationItemDataTestId}
          unreadDotDataTestId={itemUnreadDotDataTestId}
          markReadDataTestId={itemMarkReadDataTestId}
          markUnreadDataTestId={itemMarkUnreadDataTestId}
          archiveDataTestId={itemArchiveDataTestId}
          unarchiveDataTestId={itemUnarchiveDataTestId}
        />
      ))}
    </>
  );
}

export interface TabCounts {
  all: number;
  alerts: number;
  scenarios: number;
}

interface HeaderNotificationsSectionProps {
  notifications?: NotificationItem[];
  unreadCount?: number;
  tabCounts?: TabCounts;
  hasMore?: boolean;
  onOpen?: () => Promise<void> | void;
  onTabChange?: (tab: TabType) => void;
  onFilterChange?: (filter: FilterType) => void;
  onLoadMore?: () => Promise<void> | void;
  onMarkAllAsRead?: () => void;
  onArchiveAll?: () => void;
  onMarkAsRead?: (notificationId: string) => void;
  onMarkAsUnread?: (notificationId: string) => void;
  onArchive?: (notificationId: string) => void;
  onUnarchive?: (notificationId: string) => void;
  dataTestId?: string;
  triggerDataTestId?: string;
  panelDataTestId?: string;
  emptyStateDataTestId?: string;
  unreadCounterDataTestId?: string;
  notificationItemDataTestId?: string;
  tabDataTestIds?: Record<TabType, string>;
  filterTriggerDataTestId?: string;
  filterAllDataTestId?: string;
  filterUnreadDataTestId?: string;
  filterArchivedDataTestId?: string;
  moreTriggerDataTestId?: string;
  markAllReadDataTestId?: string;
  archiveAllDataTestId?: string;
  itemUnreadDotDataTestId?: string;
  itemMarkReadDataTestId?: string;
  itemMarkUnreadDataTestId?: string;
  itemArchiveDataTestId?: string;
  itemUnarchiveDataTestId?: string;
  listScrollDataTestId?: string;
}

export function HeaderNotificationsSection({
  notifications = EMPTY_NOTIFICATIONS,
  unreadCount = 0,
  tabCounts,
  hasMore = false,
  onOpen,
  onTabChange,
  onFilterChange,
  onLoadMore,
  onMarkAllAsRead,
  onArchiveAll,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  dataTestId,
  triggerDataTestId,
  panelDataTestId,
  emptyStateDataTestId,
  unreadCounterDataTestId,
  notificationItemDataTestId,
  tabDataTestIds,
  filterTriggerDataTestId,
  filterAllDataTestId,
  filterUnreadDataTestId,
  filterArchivedDataTestId,
  moreTriggerDataTestId,
  markAllReadDataTestId,
  archiveAllDataTestId,
  itemUnreadDotDataTestId,
  itemMarkReadDataTestId,
  itemMarkUnreadDataTestId,
  itemArchiveDataTestId,
  itemUnarchiveDataTestId,
  listScrollDataTestId,
}: HeaderNotificationsSectionProps) {
  const [panelState, updatePanel] = useReducer(notificationPanelReducer, INITIAL_NOTIFICATION_PANEL_STATE);
  const { isOpen, selectedTab, isFilterOpen, isThreeDotsOpen, filterType } = panelState;
  const loadingRef = useRef(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = (open: boolean) => {
    updatePanel({ isOpen: open });
    if (open) {
      void onOpen?.();
    }
  };

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || loadingRef.current) return;
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;
    if (nearBottom) {
      loadingRef.current = true;
      await Promise.resolve(onLoadMore?.()).finally(() => {
        loadingRef.current = false;
      });
    }
  };

  const handleTabChange = (tab: TabType) => {
    updatePanel({ selectedTab: tab });
    onTabChange?.(tab);
    scrollAreaRef.current?.scrollTo(0, 0);
  };

  const handleFilterSelect = (filter: FilterType) => {
    updatePanel({ filterType: filter, isFilterOpen: false });
    onFilterChange?.(filter);
  };

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead?.();
    updatePanel({ isThreeDotsOpen: false });
  };

  const handleArchiveAll = () => {
    onArchiveAll?.();
    updatePanel({ isThreeDotsOpen: false });
  };

  return (
    <SDSPopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      side="bottom"
      align="end"
      sideOffset={8}
      alignOffset={0}
      contentClassName="HeaderNotificationsSection__popover"
      showArrow={true}
      dataTestId={panelDataTestId}
      content={
        <SDSFlexbox fullWidth direction="column" className="HeaderNotificationsSection__list">
          <SDSFlexbox direction="column" fullWidth className="HeaderNotificationsSection__header">
            <SDSFlexbox
              fullWidth
              className="HeaderNotificationsSection__top_bar_menu"
              justifyContent="space-between"
              alignItems="center"
            >
              <SDSText text="Notifications" type="body-large-bold" />
              <SDSFlexbox justifyContent="flex-end" alignItems="center" gap="button-regular">
                <NotificationFilterMenu
                  filterType={filterType}
                  isFilterOpen={isFilterOpen}
                  onOpenChange={isFilterOpen => updatePanel({ isFilterOpen })}
                  onSelect={handleFilterSelect}
                  filterTriggerDataTestId={filterTriggerDataTestId}
                  filterAllDataTestId={filterAllDataTestId}
                  filterUnreadDataTestId={filterUnreadDataTestId}
                  filterArchivedDataTestId={filterArchivedDataTestId}
                />
                <NotificationOptionsMenu
                  isOpen={isThreeDotsOpen}
                  onOpenChange={isThreeDotsOpen => updatePanel({ isThreeDotsOpen })}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onArchiveAll={handleArchiveAll}
                  moreTriggerDataTestId={moreTriggerDataTestId}
                  markAllReadDataTestId={markAllReadDataTestId}
                  archiveAllDataTestId={archiveAllDataTestId}
                />
              </SDSFlexbox>
            </SDSFlexbox>

            <NotificationTabs
              selectedTab={selectedTab}
              onTabChange={handleTabChange}
              counts={filterType !== 'archived' ? tabCounts : undefined}
              dataTestId={dataTestId}
              tabDataTestIds={tabDataTestIds}
            />
          </SDSFlexbox>

          <div
            ref={scrollAreaRef}
            className="HeaderNotificationsSection__scroll-area"
            data-testid={listScrollDataTestId}
            onScroll={handleScroll}
          >
            <SDSFlexbox direction="column" fullWidth className="HeaderNotificationsSection__content">
              <NotificationListBody
                notifications={notifications}
                filterType={filterType}
                onMarkAsRead={onMarkAsRead}
                onMarkAsUnread={onMarkAsUnread}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                emptyStateDataTestId={emptyStateDataTestId}
                notificationItemDataTestId={notificationItemDataTestId}
                itemUnreadDotDataTestId={itemUnreadDotDataTestId}
                itemMarkReadDataTestId={itemMarkReadDataTestId}
                itemMarkUnreadDataTestId={itemMarkUnreadDataTestId}
                itemArchiveDataTestId={itemArchiveDataTestId}
                itemUnarchiveDataTestId={itemUnarchiveDataTestId}
              />
            </SDSFlexbox>
          </div>
        </SDSFlexbox>
      }
      className={clsx('HeaderNotificationsSection__popover-wrapper', 'HeaderNotificationsSection', {
        'HeaderNotificationsSection__popover-wrapper--active': isOpen,
      })}
      triggerDataTestId={triggerDataTestId}
      triggerProps={{ 'aria-label': 'Notifications' }}
    >
      <div
        className={clsx('HeaderNotificationsSection__icon', {
          'HeaderNotificationsSection__icon--active': isOpen,
        })}
      >
        <NotificationIcon />
      </div>
      {unreadCount > 0 && (
        <span className="HeaderNotificationsSection__counter" data-testid={unreadCounterDataTestId}>
          {unreadCount}
        </span>
      )}
    </SDSPopover>
  );
}
